// ===========================================
// Admin Dashboard Page
// ===========================================
// Enhanced dashboard with stats, activity feed, and quick actions

import { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Divider,
  Skeleton,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as TeacherTasksIcon,
  CheckCircle as AttendanceIcon,
  AttachMoney as PaymentIcon,
  Handshake as MeetAndGreetIcon,
  Add as AddIcon,
  Receipt as InvoiceIcon,
  CalendarMonth as CalendarIcon,
  ViewCompact as CompactIcon,
  ViewAgenda as ExpandedIcon,
  CloudDone as CloudSyncedIcon,
  CloudOff as CloudOffIcon,
  Warning as WarningIcon,
  Autorenew as MakeupIcon,
  Email as EmailIcon,
  QuestionAnswerOutlined as EnquiriesIcon,
  NotificationsActive as AbsenceIcon,
} from '@mui/icons-material';
import { differenceInDays, formatDistanceToNow } from 'date-fns';

import { useTerms } from '../../hooks/useAdmin';
import {
  useAdminDashboardStats,
  useActivityFeed,
  formatCurrencyRounded,
} from '../../hooks/useDashboard';
import { useAdminMakeupEligibilities } from '../../hooks/useBooking';
import { useExtraLessonPendingCount } from '../../hooks/useExtraLessonRequests';
import PageHeader from '../../components/common/PageHeader';
import { StatWidget } from '../../components/dashboard/StatWidget';
import { ActivityFeed } from '../../components/dashboard/ActivityFeed';
import { QuickActions, QuickAction } from '../../components/dashboard/QuickActions';
import EnquiryAttentionCard, { enquiryAttentionCardVisible } from '../../components/dashboard/EnquiryAttentionCard';
import RefundsDueCard from '../../components/dashboard/RefundsDueCard';
import NeedsCheckingCard from '../../components/dashboard/NeedsCheckingCard';
import MakeupEligibilitiesCard from '../../components/dashboard/MakeupEligibilitiesCard';
import HybridBookingToggle from '../../components/dashboard/HybridBookingToggle';
import { Term } from '../../api/admin.api';
import { useOnboardingConfig } from '../../hooks/useAdminSettings';
import { useOnboardingCounts } from '../../hooks/useOnboarding';
import { HowToReg as OnboardingIcon, Cake as CakeIcon } from '@mui/icons-material';
import { formatUTCDate } from '../../utils/dateUtils';
import { useUnreadCount } from '../../hooks/useMessages';
import { usePermissions } from '../../hooks/usePermissions';
import BirthdaysThisWeekModal from '../../components/dashboard/BirthdaysThisWeekModal';

// ===========================================
// COMPONENT
// ===========================================

export default function AdminDashboardPage() {
  // Compact stats toggle (persisted in localStorage)
  const [compactStats, setCompactStats] = useState<boolean>(() => {
    const saved = localStorage.getItem('adminDashboard.compactStats');
    return saved === 'true';
  });

  // Birthdays modal — the first stat card on this page to open a dialog
  // rather than navigate.
  const [birthdaysOpen, setBirthdaysOpen] = useState(false);
  const { can } = usePermissions();
  // Names and ages are student data; the bare count on the card is not, so the
  // card is always visible and only the click is gated.
  const canSeeStudents = can('people_students', 'read');
  // School-wide money (roles R16): the server sends a NULL total without invoice read, and the
  // card is not rendered (`?? 0` would show a reassuring green $0). "New Invoice" goes with it.
  const canSeeInvoices = can('operations_invoices', 'read');
  const canSeeMeetAndGreets = can('meet_and_greet', 'read');
  const canSeeEnquiries = can('enquiries', 'read');

  const toggleCompactStats = () => {
    setCompactStats((prev) => {
      const newValue = !prev;
      localStorage.setItem('adminDashboard.compactStats', String(newValue));
      return newValue;
    });
  };

  // Fetch dashboard stats from new endpoint
  const { data: stats, isLoading: statsLoading, isError: statsError } = useAdminDashboardStats();
  const enquiryCardLoading = statsLoading && can('enquiries', 'read');
  const enquiryCardError = statsError && can('enquiries', 'read');
  const enquiryCardShown = enquiryAttentionCardVisible({
    flags: stats?.enquiryFlags,
    isLoading: enquiryCardLoading,
    isError: enquiryCardError,
  });
  const { data: activityItems, isLoading: activityLoading } = useActivityFeed(10);
  const { data: terms, isLoading: termsLoading } = useTerms();
  const { data: makeupEligibilities, isLoading: eligibilitiesLoading } = useAdminMakeupEligibilities({ unredeemedOnly: true });
  const { data: unreadCountData } = useUnreadCount();
  const { data: extraLessonCountData } = useExtraLessonPendingCount();
  const unreadMessages = unreadCountData?.count ?? 0;
  const pendingExtraLessons = extraLessonCountData?.count ?? 0;

  // Onboarding config - determines M&G vs New Students widget. Both reads are gated on what their
  // routes require, so a staff role without them does not 403 on every visit (#272).
  const { data: onboardingConfigResponse } = useOnboardingConfig({ enabled: can('system_settings', 'read') });
  const useMeetAndGreetWidget = onboardingConfigResponse?.data?.requireMeetAndGreet !== false;
  const { data: onboardingCounts } = useOnboardingCounts({ enabled: canSeeMeetAndGreets });
  const pendingOnboarding = (onboardingCounts?.awaitingApproval ?? 0) + (onboardingCounts?.inProgress ?? 0);

  // Count unredeemed makeup eligibilities
  const unredeemedEligibilityCount = makeupEligibilities?.filter((e) => !e.redeemedAt).length ?? 0;

  // Get current term (must be active AND today falls within start/end dates)
  const now = new Date();
  const currentTerm = terms?.find((t: Term) => {
    if (!t.isActive) return false;
    const start = new Date(t.startDate);
    const end = new Date(t.endDate);
    return now >= start && now <= end;
  });

  // Calculate term progress
  const getTermProgress = () => {
    if (!currentTerm) return { progress: 0, daysRemaining: 0, totalDays: 0 };

    const start = new Date(currentTerm.startDate);
    const end = new Date(currentTerm.endDate);

    const totalDays = differenceInDays(end, start);
    const elapsed = differenceInDays(now, start);
    const daysRemaining = differenceInDays(end, now);
    const progress = Math.min(100, Math.max(0, (elapsed / totalDays) * 100));

    return { progress, daysRemaining, totalDays };
  };

  const termProgress = getTermProgress();

  // Determine Google Drive icon, color, and subtitle based on status
  const driveStatus = stats?.driveSyncStatus;
  const getDriveIcon = () => {
    if (!driveStatus?.isConnected) return <CloudOffIcon />;
    if (driveStatus.status === 'error') return <WarningIcon />;
    if (driveStatus.status === 'warning') return <WarningIcon />;
    return <CloudSyncedIcon />;
  };
  const getDriveColor = (): QuickAction['color'] => {
    if (!driveStatus?.isConnected) return 'secondary';
    return 'blue';
  };
  const getDriveLabel = () => {
    if (!driveStatus?.isConnected) return 'Connect Drive';
    return 'Google Drive';
  };
  const getDriveSubtitle = () => {
    if (!driveStatus?.isConnected) return undefined;
    if (!driveStatus.lastSyncAt) return 'Never synced';
    try {
      return `Synced ${formatDistanceToNow(new Date(driveStatus.lastSyncAt), { addSuffix: true })}`;
    } catch {
      return 'Synced';
    }
  };

  // Quick actions for admin
  const quickActions: QuickAction[] = [
    { label: 'Messages', subtitle: unreadMessages > 0 ? `${unreadMessages} unread` : undefined, icon: <EmailIcon />, href: 'messages', color: 'blue' },
    { label: 'New Lesson', icon: <AddIcon />, href: 'classes', color: 'yellow' },
    ...(canSeeInvoices ? ([{ label: 'New Invoice', icon: <InvoiceIcon />, href: 'invoices', color: 'mint' }] satisfies QuickAction[]) : []),
    { label: 'Meet & Greet', icon: <MeetAndGreetIcon />, href: 'meet-and-greet', color: 'coral' },
    { label: 'Calendar', icon: <CalendarIcon />, href: 'calendar', color: 'blue' },
    { label: getDriveLabel(), subtitle: getDriveSubtitle(), icon: getDriveIcon(), href: 'google-drive', color: getDriveColor() },
  ];

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Welcome to the Music 'n Me admin dashboard"
      />

      {/* Primary Stats Row - 6 widgets */}
      <Box sx={{ mb: 3 }}>
        {/* Toggle button for compact/expanded view */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title={compactStats ? 'Expand stats' : 'Compact stats'}>
            <IconButton
              size="small"
              onClick={toggleCompactStats}
              sx={{ color: 'text.secondary' }}
            >
              {compactStats ? <ExpandedIcon fontSize="small" /> : <CompactIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={compactStats ? 1 : 2}>
          {/* Outstanding Teacher Tasks */}
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Teacher Tasks"
              value={stats?.outstandingTeacherTasks ?? 0}
              icon={<TeacherTasksIcon />}
              color={(stats?.outstandingTeacherTasks ?? 0) > 0 ? 'coral' : 'success'}
              subtitle="Outstanding"
              loading={statsLoading}
              href="teachers"
              compact={compactStats}
              highlight={(stats?.outstandingTeacherTasks ?? 0) > 0}
            />
          </Grid>

          {/* Attendance Rate - Hidden for now */}
          {/* <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Attendance"
              value={`${stats?.attendanceRateThisWeek ?? 0}%`}
              icon={<AttendanceIcon />}
              color={(stats?.attendanceRateThisWeek ?? 0) >= 80 ? 'success' : 'warning'}
              subtitle="This week"
              loading={statsLoading}
              compact={compactStats}
            />
          </Grid> */}

          {/* Outstanding Payments: invoice read only (R16) */}
          {canSeeInvoices && (
            <Grid item xs={6} sm={4} md={2}>
              <StatWidget
                title="Outstanding"
                // Whole dollars only: the cents pushed this card past its grid
                // column on mobile and dragged the whole stats row off-screen.
                // Matches the parent dashboard, which already rounds.
                value={formatCurrencyRounded(stats?.totalOutstandingPayments ?? 0)}
                icon={<PaymentIcon />}
                color={(stats?.totalOutstandingPayments ?? 0) > 0 ? 'warning' : 'success'}
                loading={statsLoading}
                href="invoices?status=OVERDUE"
                compact={compactStats}
              />
            </Grid>
          )}

          {/* Enquiries: the count is null without enquiries.read, so the card is not rendered
              (a "0" would be false, and the link goes to a page the caller cannot open) (D4). */}
          {canSeeEnquiries && (
            <Grid item xs={6} sm={4} md={2}>
              <StatWidget
                title="Enquiries"
                value={stats?.pendingEnquiries ?? 0}
                icon={<EnquiriesIcon />}
                color="primary"
                subtitle="New"
                loading={statsLoading}
                href="enquiries?tab=NEW"
                compact={compactStats}
                highlight={(stats?.pendingEnquiries ?? 0) > 0}
              />
            </Grid>
          )}

          {/* Pending Meet & Greets / New Students (dynamic based on config). Both figures need
              meet_and_greet.read: without it the stats send null M&G counts (#292 D8) and the
              onboarding counts are never fetched (#272), so the card would show a false "0" or a
              skeleton that never resolves. Not rendered at all, like the Outstanding card. */}
          {canSeeMeetAndGreets && (
            <Grid item xs={6} sm={4} md={2}>
              {useMeetAndGreetWidget ? (
                <StatWidget
                  title="Pending M&G"
                  value={stats?.pendingMeetAndGreets ?? 0}
                  icon={<MeetAndGreetIcon />}
                  color={(stats?.pendingMeetAndGreets ?? 0) > 0 ? 'info' : 'success'}
                  subtitle={stats?.upcomingMeetAndGreets ? `${stats.upcomingMeetAndGreets} scheduled` : undefined}
                  loading={statsLoading}
                  href="meet-and-greet"
                  compact={compactStats}
                  highlight={(stats?.pendingMeetAndGreets ?? 0) > 0}
                />
              ) : (
                <StatWidget
                  title="New Students"
                  value={pendingOnboarding}
                  icon={<OnboardingIcon />}
                  color={pendingOnboarding > 0 ? 'info' : 'success'}
                  subtitle={onboardingCounts?.awaitingApproval ? `${onboardingCounts.awaitingApproval} awaiting approval` : undefined}
                  loading={!onboardingCounts}
                  href="new-students"
                  compact={compactStats}
                  highlight={pendingOnboarding > 0}
                />
              )}
            </Grid>
          )}

          {/* Make-up Eligibilities */}
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Make-ups"
              value={unredeemedEligibilityCount}
              icon={<MakeupIcon />}
              color={unredeemedEligibilityCount > 0 ? 'warning' : 'success'}
              subtitle="Pending"
              loading={statsLoading || eligibilitiesLoading}
              href="students?filter=makeup"
              compact={compactStats}
              highlight={unredeemedEligibilityCount > 0}
            />
          </Grid>

          {/* Extra Lessons */}
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Extra Lessons"
              value={pendingExtraLessons}
              icon={<SchoolIcon />}
              color="info"
              subtitle="Pending"
              loading={statsLoading}
              href="extra-lessons"
              compact={compactStats}
              highlight={pendingExtraLessons > 0}
            />
          </Grid>

          {/* Absences */}
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Absences"
              value={stats?.pendingAbsences ?? 0}
              icon={<AbsenceIcon />}
              color={(stats?.pendingAbsences ?? 0) > 0 ? 'coral' : 'success'}
              subtitle="Active"
              loading={statsLoading}
              href="absences"
              compact={compactStats}
              highlight={(stats?.pendingAbsences ?? 0) > 0}
            />
          </Grid>

          {/* Birthdays this week (2026-09-07) */}
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Birthdays"
              value={stats?.birthdaysThisWeek ?? 0}
              icon={<CakeIcon />}
              color="yellow"
              subtitle="This week"
              loading={statsLoading}
              compact={compactStats}
              onClick={canSeeStudents ? () => setBirthdaysOpen(true) : undefined}
              highlight={(stats?.birthdaysThisWeek ?? 0) > 0}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Secondary Row - Quick Actions and Activity Feed */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <HybridBookingToggle />
          <QuickActions actions={quickActions} columns={2} />
        </Grid>

        {/* Activity Feed */}
        <Grid item xs={12} md={8}>
          <ActivityFeed
            items={activityItems ?? []}
            loading={activityLoading}
            maxItems={5}
            title="Recent Activity"
          />
        </Grid>
      </Grid>

      {/* Enquiries needing attention: absent when the server sends no flags (no enquiries.read) */}
      {/* N1: the margin box exists only when the card does (it returns null otherwise). */}
      {enquiryCardShown && (
        <Box sx={{ mb: 3, minWidth: 0 }} data-testid="enquiry-attention-slot">
          <EnquiryAttentionCard flags={stats?.enquiryFlags} isLoading={enquiryCardLoading} isError={enquiryCardError} />
        </Box>
      )}

      {/* #642: open Refunds due (operations_refunds.write; the card returns null otherwise or when empty) */}
      <Box sx={{ mb: 3, minWidth: 0, '&:empty': { display: 'none', mb: 0 } }}>
        <RefundsDueCard />
      </Box>
      {/* #642 Phase 2 Request 7: payments the owner flagged to check (owner only; null when none) */}
      <Box sx={{ mb: 3, minWidth: 0, '&:empty': { display: 'none', mb: 0 } }}>
        <NeedsCheckingCard />
      </Box>

      {/* Third Row - Current Term */}
      <Grid container spacing={3}>
        {/* Current Term Card with Progress */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="primary" />
                  <Typography variant="h6">Current Term</Typography>
                </Box>
              }
              action={
                currentTerm && (
                  <Chip
                    label="Active"
                    size="small"
                    sx={{
                      bgcolor: '#c5ebe2',
                      color: '#5cb399',
                      fontWeight: 600,
                    }}
                  />
                )
              }
              sx={{ pb: 0 }}
            />
            <Divider sx={{ mx: 2, mt: 2 }} />
            <CardContent>
              {termsLoading ? (
                <Box>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="rectangular" height={8} sx={{ mt: 2, borderRadius: 1 }} />
                </Box>
              ) : currentTerm ? (
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {currentTerm.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {formatUTCDate(currentTerm.startDate, { day: 'numeric', month: 'short', year: 'numeric' })} -{' '}
                    {formatUTCDate(currentTerm.endDate, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </Typography>

                  {/* Progress Bar */}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Term Progress
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {Math.round(termProgress.progress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={termProgress.progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: termProgress.progress > 80 ? '#FFAE9E' : '#4580E4',
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {termProgress.daysRemaining > 0
                        ? `${termProgress.daysRemaining} days remaining`
                        : 'Term has ended'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">No active term</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Configure a term in Settings &gt; Terms
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Make-up Eligibilities Card */}
        <Grid item xs={12} md={6}>
          <MakeupEligibilitiesCard
            eligibilities={makeupEligibilities ?? []}
            isLoading={eligibilitiesLoading}
            maxItems={5}
          />
        </Grid>
      </Grid>

      {/* Fourth Row - Monthly Overview (Full Width) */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttendanceIcon color="primary" />
                  <Typography variant="h6">Monthly Overview</Typography>
                </Box>
              }
              sx={{ pb: 0 }}
            />
            <Divider sx={{ mx: 2, mt: 2 }} />
            <CardContent>
              {statsLoading ? (
                <Box sx={{ display: 'flex', gap: 4, justifyContent: 'space-around' }}>
                  <Skeleton variant="rectangular" width={100} height={60} />
                  <Skeleton variant="rectangular" width={100} height={60} />
                  <Skeleton variant="rectangular" width={100} height={60} />
                  <Skeleton variant="rectangular" width={100} height={60} />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="h4" color="primary.main" fontWeight="bold">
                        {stats?.attendanceRateThisMonth ?? 0}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monthly Attendance
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="h4" color="secondary.main" fontWeight="bold">
                        {stats?.totalLessonsThisWeek ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lessons This Week
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {stats?.totalActiveFamilies ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Families
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          color: (stats?.pendingMeetAndGreets ?? 0) > 0 ? '#e67761' : '#5cb399',
                        }}
                      >
                        {(stats?.pendingMeetAndGreets ?? 0) + (stats?.upcomingMeetAndGreets ?? 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Meet & Greets
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {canSeeStudents && (
        <BirthdaysThisWeekModal
          open={birthdaysOpen}
          onClose={() => setBirthdaysOpen(false)}
          missingBirthDateCount={stats?.studentsMissingBirthDate ?? 0}
          studentsPath="students"
        />
      )}
    </Box>
  );
}
